import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { User, Storefront, LinkSimple, Crown, Plus, Trash, FloppyDisk, ArrowRight, Globe, Lightning, Wrench, Warning, CheckCircle, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import UsagePage from './UsagePage';

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_TYPES = [
  { id: 'garn', label: 'Garn' },
  { id: 'oppskrift', label: 'Oppskrift' },
  { id: 'strikkepakke', label: 'Strikkepakke' },
  { id: 'annet', label: 'Annet' },
];

export default function SettingsPage() {
  const getHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    [],
  );

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-extrabold">Innstillinger</h1>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
          Profil, butikkforbindelse, lenkeregler og premium-tillegg samlet på ett sted.
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1 pb-1 [-webkit-overflow-scrolling:touch]">
          <TabsList className="w-max">
            <TabsTrigger value="profile" className="gap-1"><User size={14} /> Profil</TabsTrigger>
            <TabsTrigger value="store" className="gap-1"><Storefront size={14} /> WooCommerce</TabsTrigger>
            <TabsTrigger value="links" className="gap-1"><LinkSimple size={14} /> Lenkeregler</TabsTrigger>
            <TabsTrigger value="usage" className="gap-1" data-testid="tab-usage"><Lightning size={14} /> Forbruk</TabsTrigger>
            <TabsTrigger value="premium" className="gap-1"><Crown size={14} /> Premium</TabsTrigger>
            <TabsTrigger value="maintenance" className="gap-1" data-testid="tab-maintenance"><Wrench size={14} /> Vedlikehold</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="profile"><ProfileTab getHeaders={getHeaders} /></TabsContent>
        <TabsContent value="store"><StoreTab getHeaders={getHeaders} /></TabsContent>
        <TabsContent value="links"><LinksTab getHeaders={getHeaders} /></TabsContent>
        <TabsContent value="usage"><UsagePage embedded /></TabsContent>
        <TabsContent value="premium"><PremiumTab /></TabsContent>
        <TabsContent value="maintenance"><MaintenanceTab getHeaders={getHeaders} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Profile ────────────────────────────────────────────────
function ProfileTab({ getHeaders }) {
  const [me, setMe] = useState(null);
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyCity, setCompanyCity] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/auth/me`, { headers: getHeaders() });
        setMe(data);
        setName(data.name || '');
        setCompanyName(data.company_name || '');
        setCompanyCity(data.company_city || '');
      } catch {}
    })();
  }, [getHeaders]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/auth/me`,
        { name, company_name: companyName, company_city: companyCity },
        { headers: getHeaders() },
      );
      toast.success('Lagret');
    } catch {
      toast.error('Kunne ikke lagre');
    } finally {
      setSaving(false);
    }
  };

  if (!me) return <div className="h-32 bg-muted animate-pulse rounded-md" />;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading text-lg">Min profil</CardTitle>
        <p className="text-xs text-muted-foreground">
          Firmanavn og by brukes i AI-genererte tekster for lokal SEO ("sendes fra vårt lager i {companyCity || '[by]'}").
        </p>
      </CardHeader>
      <CardContent className="space-y-4 max-w-xl">
        <Field label="E-post (kan ikke endres)" value={me.email} readOnly />
        <Field label="Navn" value={name} onChange={setName} placeholder="Ditt navn" />
        <Field label="Firmanavn" value={companyName} onChange={setCompanyName} placeholder="F.eks. Tittinngarn" />
        <Field label="By / sted" value={companyCity} onChange={setCompanyCity} placeholder="F.eks. Bergen" />
        <Button onClick={save} disabled={saving} data-testid="save-profile-btn" className="gap-1">
          <FloppyDisk size={14} /> {saving ? 'Lagrer...' : 'Lagre'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Store / WooCommerce ────────────────────────────────────
function StoreTab({ getHeaders }) {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({
    woo_url: '', woo_consumer_key: '', woo_consumer_secret: '',
    wp_username: '', wp_app_password: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/tenants/me`, { headers: getHeaders() });
        const t = data.tenant || data;
        setTenant(t);
        setForm({
          woo_url: t.woo_url || '',
          woo_consumer_key: '',
          woo_consumer_secret: '',
          wp_username: t.wp_username || '',
          wp_app_password: '',
        });
      } catch {}
    })();
  }, [getHeaders]);

  const save = async () => {
    setSaving(true);
    try {
      // Use existing /tenants/setup endpoint (creates or updates).
      const payload = {
        name: tenant?.name || 'Min butikk',
        woo_url: form.woo_url,
        wp_username: form.wp_username,
        // setup expects keys; fall back to existing if not changed
        woo_consumer_key: form.woo_consumer_key || '__keep__',
        woo_consumer_secret: form.woo_consumer_secret || '__keep__',
        wp_app_password: form.wp_app_password || '__keep__',
      };
      await axios.post(`${API}/api/tenants/setup`, payload, { headers: getHeaders() });
      toast.success('Tilkoblingsinnstillinger lagret');
    } catch (e) {
      toast.error('Kunne ikke lagre');
    } finally {
      setSaving(false);
    }
  };

  if (!tenant) return <div className="h-32 bg-muted animate-pulse rounded-md" />;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Storefront size={20} /> WooCommerce-tilkobling</CardTitle>
        <p className="text-xs text-muted-foreground">
          Hemmeligheter vises ikke etter lagring (sikkerhet). La feltene være tomme for å beholde nåværende verdier.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 max-w-xl">
        <Field label="Butikk-URL" value={form.woo_url} onChange={(v) => setForm({ ...form, woo_url: v })} placeholder="https://tittinngarn.no" />
        <Field label="Consumer Key" value={form.woo_consumer_key} onChange={(v) => setForm({ ...form, woo_consumer_key: v })} placeholder="ck_..." />
        <Field label="Consumer Secret" value={form.woo_consumer_secret} onChange={(v) => setForm({ ...form, woo_consumer_secret: v })} placeholder="cs_..." type="password" />
        <Field label="WP-brukernavn" value={form.wp_username} onChange={(v) => setForm({ ...form, wp_username: v })} placeholder="admin" />
        <Field label="WP App-passord" value={form.wp_app_password} onChange={(v) => setForm({ ...form, wp_app_password: v })} placeholder="xxxx xxxx xxxx" type="password" />
        <Button onClick={save} disabled={saving} className="gap-1" data-testid="save-store-btn">
          <FloppyDisk size={14} /> {saving ? 'Lagrer...' : 'Lagre'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Linking rules ───────────────────────────────────────────
function LinksTab({ getHeaders }) {
  const [internal, setInternal] = useState(null);
  const [external, setExternal] = useState([]);
  const [pool, setPool] = useState([]);
  const [attrs, setAttrs] = useState([]);   // synced WC attributes (for dropdowns)
  const [cats, setCats] = useState([]);     // top-level categories from products
  const [newPoolItem, setNewPoolItem] = useState(EMPTY_POOL_ITEM());
  const [newExt, setNewExt] = useState({ supplier_term: '', url: '', anchor_text: '', applies_to_types: ['garn'] });

  const load = useCallback(async () => {
    try {
      const [r, e, p, a, c] = await Promise.all([
        axios.get(`${API}/api/attributes/linking-rules`, { headers: getHeaders() }),
        axios.get(`${API}/api/attributes/external-links`, { headers: getHeaders() }),
        axios.get(`${API}/api/attributes/site-links`, { headers: getHeaders() }),
        axios.get(`${API}/api/attributes`, { headers: getHeaders() }),
        axios.get(`${API}/api/products/categories/list`, { headers: getHeaders() }),
      ]);
      setInternal(r.data);
      setExternal(e.data);
      setPool(p.data);
      setAttrs(a.data);
      setCats(c.data);
    } catch {}
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const toggleRule = async (id, enabled) => {
    const next = {
      ...internal,
      rules: internal.rules.map((r) => (r.id === id ? { ...r, enabled } : r)),
    };
    setInternal(next);
    try {
      await axios.put(`${API}/api/attributes/linking-rules`, next, { headers: getHeaders() });
    } catch { toast.error('Kunne ikke lagre'); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Slett denne regelen?')) return;
    const next = { ...internal, rules: internal.rules.filter((r) => r.id !== id) };
    setInternal(next);
    try {
      await axios.put(`${API}/api/attributes/linking-rules`, next, { headers: getHeaders() });
    } catch { toast.error('Kunne ikke slette'); }
  };

  const saveExternal = async (item) => {
    try {
      await axios.put(`${API}/api/attributes/external-links`, item, { headers: getHeaders() });
      toast.success(`${item.supplier_term} lagret`);
      load();
    } catch { toast.error('Kunne ikke lagre'); }
  };

  const deleteExternal = async (term) => {
    if (!window.confirm(`Slett ekstern lenke for «${term}»?`)) return;
    try {
      await axios.delete(`${API}/api/attributes/external-links/${encodeURIComponent(term)}`, { headers: getHeaders() });
      load();
    } catch { toast.error('Kunne ikke slette'); }
  };

  const savePoolItem = async (item) => {
    try {
      await axios.put(`${API}/api/attributes/site-links`, item, { headers: getHeaders() });
      toast.success('Lagret');
      load();
    } catch { toast.error('Kunne ikke lagre'); }
  };

  const deletePoolItem = async (id) => {
    if (!window.confirm('Slett denne lenken?')) return;
    try {
      await axios.delete(`${API}/api/attributes/site-links/${id}`, { headers: getHeaders() });
      load();
    } catch { toast.error('Kunne ikke slette'); }
  };

  return (
    <div className="space-y-6">
      {/* Site links pool */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <LinkSimple size={18} /> Lenkebibliotek
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Legg inn URL-er fra nettsiden din. <strong>Vanlig lenke</strong> = enkel lenke. <strong>Smart kategori-lenke</strong> = AI bygger automatisk filter-URL-er fra produktets attributter (f.eks. /strikkepakker/filters/leverandor/sandnes/).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {pool.length === 0 && (
            <p className="text-xs text-muted-foreground italic">Ingen lenker enda. Legg til den første under.</p>
          )}
          {pool.map((p) => (
            <PoolRow
              key={p.id}
              item={p}
              attrs={attrs}
              cats={cats}
              onSave={savePoolItem}
              onDelete={() => deletePoolItem(p.id)}
            />
          ))}
          <div className="border-t pt-4 mt-2">
            <p className="text-sm font-medium mb-3">Legg til ny lenke</p>
            <PoolEditor
              draft={newPoolItem}
              setDraft={setNewPoolItem}
              attrs={attrs}
              cats={cats}
            />
            <Button
              size="sm"
              className="mt-3 gap-1"
              onClick={() => {
                if (!newPoolItem.title || !newPoolItem.url) {
                  toast.error('Fyll inn tittel og URL');
                  return;
                }
                savePoolItem({ ...newPoolItem, enabled: true });
                setNewPoolItem(EMPTY_POOL_ITEM());
              }}
              data-testid="add-pool-link-btn"
            >
              <Plus size={14} /> Legg til
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* External supplier links */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Globe size={18} /> Eksterne autoritetslenker
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Per leverandør. Eksempel: «sandnes-garn» → <code className="bg-muted px-1 rounded">https://www.sandnesgarn.no/baerekraft</code>. AI vever disse inn i beskrivelsen for E-E-A-T-styrking. Bruk leverandør-term-slug fra WooCommerce (små bokstaver, bindestrek).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {external.map((e) => (
            <ExternalRow
              key={e.supplier_term}
              item={e}
              onSave={saveExternal}
              onDelete={() => deleteExternal(e.supplier_term)}
            />
          ))}
          <div className="border-t pt-4 mt-2">
            <p className="text-sm font-medium mb-2">Legg til ny</p>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Leverandør (term-slug)" value={newExt.supplier_term} onChange={(v) => setNewExt({ ...newExt, supplier_term: v })} placeholder="f.eks. sandnes-garn" />
              <Field label="Lenketekst" value={newExt.anchor_text} onChange={(v) => setNewExt({ ...newExt, anchor_text: v })} placeholder="f.eks. Les om Sandnes Garns bærekraft" />
              <div className="md:col-span-2">
                <Field label="URL" value={newExt.url} onChange={(v) => setNewExt({ ...newExt, url: v })} placeholder="https://www.sandnesgarn.no/baerekraft" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm">Gjelder produkttyper</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PRODUCT_TYPES.map((t) => (
                    <Badge
                      key={t.id}
                      onClick={() => {
                        const has = newExt.applies_to_types.includes(t.id);
                        setNewExt({
                          ...newExt,
                          applies_to_types: has
                            ? newExt.applies_to_types.filter((x) => x !== t.id)
                            : [...newExt.applies_to_types, t.id],
                        });
                      }}
                      className={`cursor-pointer ${
                        newExt.applies_to_types.includes(t.id)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {t.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <Button
              size="sm"
              className="mt-3 gap-1"
              onClick={() => {
                if (!newExt.supplier_term || !newExt.url) {
                  toast.error('Fyll inn leverandør og URL');
                  return;
                }
                saveExternal({ ...newExt, enabled: true });
                setNewExt({ supplier_term: '', url: '', anchor_text: '', applies_to_types: ['garn'] });
              }}
              data-testid="add-external-link-btn"
            >
              <Plus size={14} /> Legg til
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EMPTY_POOL_ITEM() {
  return {
    title: '', url: '', hint: '',
    mode: 'static',
    category_filter: {
      category_slugs: [],
      filter_url_pattern: 'filters/{attr_slug}/{term_slug}/',
      attribute_links: [],
    },
  };
}

function PoolEditor({ draft, setDraft, attrs, cats }) {
  const updateCat = (slug, on) => {
    const cur = draft.category_filter?.category_slugs || [];
    const next = on ? [...new Set([...cur, slug])] : cur.filter((s) => s !== slug);
    setDraft({ ...draft, category_filter: { ...draft.category_filter, category_slugs: next } });
  };
  const addAttrLink = () => {
    const list = draft.category_filter?.attribute_links || [];
    setDraft({
      ...draft,
      category_filter: {
        ...draft.category_filter,
        attribute_links: [...list, { attribute_slug: '', anchor_template: '' }],
      },
    });
  };
  const updateAttrLink = (i, k, v) => {
    const list = [...(draft.category_filter?.attribute_links || [])];
    list[i] = { ...list[i], [k]: v };
    setDraft({ ...draft, category_filter: { ...draft.category_filter, attribute_links: list } });
  };
  const removeAttrLink = (i) => {
    const list = (draft.category_filter?.attribute_links || []).filter((_, idx) => idx !== i);
    setDraft({ ...draft, category_filter: { ...draft.category_filter, attribute_links: list } });
  };

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={draft.mode === 'static' ? 'default' : 'outline'}
          onClick={() => setDraft({ ...draft, mode: 'static' })}
          data-testid="mode-static-btn"
        >
          Vanlig lenke
        </Button>
        <Button
          size="sm"
          variant={draft.mode === 'category_filter' ? 'default' : 'outline'}
          onClick={() => setDraft({ ...draft, mode: 'category_filter' })}
          data-testid="mode-smart-btn"
        >
          🧠 Smart kategori-lenke
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Field
          label={draft.mode === 'category_filter' ? 'Tittel (vises som basis-lenke)' : 'Tittel'}
          value={draft.title}
          onChange={(v) => setDraft({ ...draft, title: v })}
          placeholder={draft.mode === 'category_filter' ? 'F.eks. Se alle strikkepakker' : 'F.eks. Bestselgere'}
        />
        <Field
          label={draft.mode === 'category_filter' ? 'Base-URL (kategoriside)' : 'URL'}
          value={draft.url}
          onChange={(v) => setDraft({ ...draft, url: v })}
          placeholder={draft.mode === 'category_filter' ? 'https://dinbutikk.no/kategori/strikkepakker/' : 'https://dinbutikk.no/bestselgere'}
        />
        <div className="md:col-span-2">
          <Field
            label="Hint til AI (valgfritt)"
            value={draft.hint}
            onChange={(v) => setDraft({ ...draft, hint: v })}
            placeholder="F.eks. «når produktet er en kampanjevare»"
          />
        </div>
      </div>

      {draft.mode === 'category_filter' && (
        <div className="border border-border rounded-md p-3 bg-muted/20 space-y-3">
          <div>
            <Label className="text-sm font-medium">Brukes kun når produktet er i én av disse kategoriene</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {(cats || []).map((c) => {
                const slug = c.slug || c.name?.toLowerCase().replace(/\s+/g, '-');
                const on = (draft.category_filter?.category_slugs || []).includes(slug);
                return (
                  <Badge
                    key={slug}
                    onClick={() => updateCat(slug, !on)}
                    className={`cursor-pointer ${on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                  >
                    {c.name}
                  </Badge>
                );
              })}
              {(!cats || cats.length === 0) && (
                <p className="text-xs text-muted-foreground italic">Ingen kategorier synket. Kjør sync først.</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Tom = brukes på alle produkter med disse kategoriene.</p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Auto-bygg filter-lenker per attributt</Label>
              <Button size="sm" variant="outline" onClick={addAttrLink} className="gap-1">
                <Plus size={12} /> Legg til
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1 mb-2">
              For hvert attributt produktet har, bygges en URL automatisk: <code className="bg-muted px-1 rounded">base + filters/&#123;attr&#125;/&#123;term&#125;/</code>
            </p>
            {(draft.category_filter?.attribute_links || []).map((al, i) => (
              <div key={i} className="grid md:grid-cols-[1fr_1.5fr_auto] gap-2 mb-2 items-end">
                <div>
                  <Label className="text-xs">Attributt</Label>
                  <select
                    value={al.attribute_slug}
                    onChange={(e) => updateAttrLink(i, 'attribute_slug', e.target.value)}
                    className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
                  >
                    <option value="">– Velg –</option>
                    {(attrs || []).map((a) => (
                      <option key={a.slug} value={a.slug}>{a.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Lenketekst-mal</Label>
                  <Input
                    value={al.anchor_template}
                    onChange={(e) => updateAttrLink(i, 'anchor_template', e.target.value)}
                    placeholder="F.eks. Se flere strikkepakker fra {term_name}"
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeAttrLink(i)} className="text-destructive">
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>

          <div className="text-xs text-muted-foreground bg-background p-2 rounded border">
            💡 <strong>Eksempel:</strong> Et Sandnes Sisu strikkepakke-produkt får automatisk lenker som
            «<em>Se flere strikkepakker fra Sandnes Garn</em>» og «<em>Se flere strikkepakker i Sisu</em>».
          </div>
        </div>
      )}
    </div>
  );
}

function PoolRow({ item, attrs, cats, onSave, onDelete }) {
  const [edit, setEdit] = useState(item);
  const [expanded, setExpanded] = useState(false);
  const dirty = JSON.stringify(edit) !== JSON.stringify(item);
  const isSmart = (item.mode || 'static') === 'category_filter';

  return (
    <div className="border border-border rounded-md p-3 space-y-2" data-testid={`pool-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-sm">{item.title}</p>
            {isSmart && <Badge className="text-[10px] bg-amber-100 text-amber-800">🧠 Smart</Badge>}
          </div>
          <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">{item.url}</a>
          {item.hint && <p className="text-xs text-muted-foreground italic mt-1">Hint: {item.hint}</p>}
          {isSmart && item.category_filter?.attribute_links?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Bygger filter-lenker for {item.category_filter.attribute_links.length} attributter:{' '}
              {item.category_filter.attribute_links.map((al) => al.attribute_slug).join(', ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={edit.enabled} onCheckedChange={(v) => setEdit({ ...edit, enabled: v })} />
          <Button variant="ghost" size="sm" onClick={() => setExpanded((x) => !x)}>
            {expanded ? 'Lukk' : 'Rediger'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} className="text-destructive">
            <Trash size={14} />
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="pt-2 border-t">
          <PoolEditor draft={edit} setDraft={setEdit} attrs={attrs} cats={cats} />
        </div>
      )}
      {dirty && (
        <Button size="sm" onClick={() => { onSave(edit); setExpanded(false); }} className="gap-1">
          <FloppyDisk size={14} /> Lagre endringer
        </Button>
      )}
    </div>
  );
}

function ExternalRow({ item, onSave, onDelete }) {
  const [edit, setEdit] = useState(item);
  const dirty = JSON.stringify(edit) !== JSON.stringify(item);
  return (
    <div className="border border-border rounded-md p-3 space-y-2" data-testid={`ext-${item.supplier_term}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-medium text-sm">{item.supplier_term}</p>
          <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">{item.url}</a>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={edit.enabled} onCheckedChange={(v) => setEdit({ ...edit, enabled: v })} />
          <Button variant="ghost" size="sm" onClick={onDelete} className="text-destructive">
            <Trash size={14} />
          </Button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        <Input value={edit.url} onChange={(e) => setEdit({ ...edit, url: e.target.value })} placeholder="URL" />
        <Input value={edit.anchor_text || ''} onChange={(e) => setEdit({ ...edit, anchor_text: e.target.value })} placeholder="Lenketekst" />
      </div>
      {dirty && (
        <Button size="sm" onClick={() => onSave(edit)} className="gap-1">
          <FloppyDisk size={14} /> Lagre endringer
        </Button>
      )}
    </div>
  );
}

// ─── Premium ────────────────────────────────────────────────
function PremiumTab() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Crown size={20} weight="fill" className="text-amber-500" /> Premium oppgraderinger
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border border-amber-300 bg-gradient-to-br from-amber-50 to-pink-50 p-5 rounded-md">
          <p className="font-heading text-xl font-bold flex items-center gap-2">
            <Crown size={20} weight="fill" className="text-amber-500" /> Premium AI Pack
          </p>
          <p className="text-sm text-muted-foreground mt-2 mb-4">
            Bytter standard AI-modell (Gemini 3 Pro) til <strong>Claude Sonnet 4.5</strong> for enda bedre språk, SEO-tone og HTML-struktur. Tillegg: kr 199/mnd.
          </p>
          <Button disabled>Aktiver — kommer med Stripe</Button>
        </div>

        <div className="border p-5 rounded-md">
          <p className="font-heading text-xl font-bold">Topp-opp optimaliseringer</p>
          <p className="text-sm text-muted-foreground mt-2 mb-4">
            Trenger du flere optimaliseringer denne måneden? Kjøp ekstra på Forbruk-siden.
          </p>
          <Button asChild variant="outline" className="gap-1">
            <Link to="/usage">Se forbruk og topp-opp <ArrowRight size={14} /></Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Helpers ────────────────────────────────────────────────
function Field({ label, value, onChange, readOnly, placeholder, type = 'text' }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <Input
        type={type}
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        className={readOnly ? 'bg-muted text-muted-foreground' : ''}
      />
    </div>
  );
}


// ─── Maintenance ────────────────────────────────────────────
// Temporary tools the user can run when something needs back-filling.
// Each section is self-contained so it can be removed cleanly once obsolete.
function MaintenanceTab({ getHeaders }) {
  return (
    <div className="space-y-4">
      <SeoMetaBackfillCard getHeaders={getHeaders} />
    </div>
  );
}

function SeoMetaBackfillCard({ getHeaders }) {
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sync/seo-meta-backfill/status`, { headers: getHeaders() });
      setStatus(data);
    } catch {}
  }, [getHeaders]);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 4000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const start = async (dryRun) => {
    if (!dryRun && !window.confirm(
      'Dette pusher meta_title, meta_description og focus_keyword på alle produkter som har lagrede verdier ' +
      '(de som tidligere ble droppet av WooCommerce-buggen). Trygt å kjøre. Fortsette?'
    )) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/sync/seo-meta-backfill${dryRun ? '?dry_run=true' : ''}`,
        {},
        { headers: getHeaders() },
      );
      toast.success(data.message);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill feilet');
    } finally {
      setSubmitting(false);
    }
  };

  const isRunning = !!status?.running;
  const hasFinished = status?.finished_at && !isRunning;
  const total = status?.total || 0;
  const done = (status?.ok || 0) + (status?.fail || 0) + (status?.skipped || 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <Card data-testid="seo-meta-backfill-card" className="border-2 border-warning/40">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Warning size={18} weight="bold" className="text-warning" />
          Reparér Yoast-meta (engangsjobb)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong className="text-foreground">Bakgrunn:</strong> Inntil i dag sendte Sortilize meta_title og meta_description som flate WooCommerce-felter — som ble silently droppet. Yoast Premium har derfor aldri mottatt de optimaliserte titlene, beskrivelsene eller fokusnøkkelfrasene som ble generert.
          </p>
          <p>
            <strong className="text-foreground">Hva denne knappen gjør:</strong> Pusher meta_title, meta_description og focus_keyword på alle produkter som har lagrede verdier i Sortilize-databasen — som <code>meta_data</code>-array Yoast og Rank Math leser. Trygt å re-kjøre. Kan slettes etter at den er kjørt.
          </p>
        </div>

        {status && total > 0 && (
          <div className="space-y-2 p-3 rounded-md border border-border bg-muted/30" data-testid="backfill-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">
                {isRunning ? 'Kjører…' : hasFinished ? 'Ferdig' : 'Klar'}
                {status.dry_run && <Badge variant="outline" className="ml-2 text-[10px]">DRY-RUN</Badge>}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {done} / {total} ({pct}%)
              </span>
            </div>
            <div className="h-2 bg-background rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${isRunning ? 'bg-primary animate-pulse' : 'bg-success'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="flex items-center gap-1 text-success">
                <CheckCircle size={12} weight="bold" /> {status.ok || 0} ok
              </div>
              <div className="flex items-center gap-1 text-destructive">
                <Warning size={12} weight="bold" /> {status.fail || 0} feilet
              </div>
              <div className="flex items-center gap-1 text-muted-foreground">
                {status.skipped || 0} hoppet over
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => start(true)}
            disabled={submitting || isRunning}
            className="gap-1"
            data-testid="backfill-dryrun-btn"
          >
            <ArrowsClockwise size={14} /> Vis hva som ville blitt pushet
          </Button>
          <Button
            onClick={() => start(false)}
            disabled={submitting || isRunning}
            className="gap-1"
            data-testid="backfill-start-btn"
          >
            <ArrowRight size={14} /> Kjør reparasjon
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
